import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '../../helpers/lazyWithRetry';
import ErrorState from './ErrorState';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleGeneralErrorRetry = () => {
    const { onReset } = this.props;

    this.setState({ hasError: false, error: null });

    if (onReset) {
      onReset();
    }
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      if (isChunkLoadError(error)) {
        return (
          <div className="d-flex flex-column align-items-center justify-content-center py-20">
            <i className="ki-duotone ki-arrows-circle fs-5x text-primary mb-5">
              <span className="path1" />
              <span className="path2" />
            </i>
            <h3 className="text-gray-800 mb-3">Nova versão disponível</h3>
            <p className="text-gray-600 text-center mb-5" style={{ maxWidth: 500 }}>
              Não foi possível carregar esta página. Recarregue para obter a versão mais recente.
            </p>
            <button className="btn btn-sm btn-primary" type="button" onClick={() => window.location.reload()}>
              Recarregar página
            </button>
          </div>
        );
      }

      return <ErrorState error={error} onRetry={this.handleGeneralErrorRetry} />;
    }

    return children;
  }
}

export default ErrorBoundary;
